import { Builder } from "./builder";
import { CAIPValidationError } from "./errors";
import {
  detectChainNamespace,
  EVM_NATIVE_SLIP44_COIN_TYPE,
  EVM_NATIVE_TOKEN_PLACEHOLDER_ADDRESS,
  NAMESPACES,
  SOLANA_CHAIN_ID,
  SOLANA_CHAIN_REFERENCE,
  SOLANA_NATIVE_SLIP44_COIN_TYPE,
  SOLANA_NATIVE_TOKEN_ADDRESS,
} from "./constants";
import { Parser } from "./parser";
import type {
  AccountId,
  AssetId,
  AssetIdentifier,
  AssetReference,
  ChainReference,
} from "./types";
import { normalizeAssetReference } from "./normalization";
export {
  normalizeAccountAddress,
  normalizeAssetReference,
} from "./normalization";

/**
 * Normalize a CAIP-10 account ID by decomposing, normalizing the address, and recomposing.
 * - EVM (eip155): lowercases the address
 * - Solana: preserves the original case (Base58 is case-sensitive)
 */
export const normalizeCaipAccountId = (caipAccountId: string): AccountId => {
  const parsed = Parser.parseAccountId(caipAccountId);
  return Builder.buildAccountId(parsed.chain, parsed.account);
};

export const normalizeCaipAccountIdIfPossible = (
  caipAccountId: string,
): string => {
  try {
    return normalizeCaipAccountId(caipAccountId);
  } catch {
    return caipAccountId;
  }
};

/**
 * Canonicalize either a CAIP-19 asset id or a legacy asset id.
 *
 * CAIP-19 components are reserialized through the namespace-aware builder so
 * EVM chain references and hex asset references retain their existing
 * lowercase canonical form while Solana Base58 references remain untouched.
 * Legacy/non-CAIP ids intentionally keep the historical lowercase fallback;
 * changing that fallback would change existing fingerprints and resource keys.
 */
export const canonicalizeAssetIdLike = (assetId: string): string => {
  try {
    const parsed = Parser.parseAssetId(assetId);
    const chain =
      parsed.chain.namespace === "eip155"
        ? {
            ...parsed.chain,
            reference: parsed.chain.reference.toLowerCase(),
          }
        : parsed.chain;
    return Builder.buildAssetId(chain, parsed.asset);
  } catch {
    return assetId.toLowerCase();
  }
};

/** One execution identity for native value, preserving the asset's chain. */
export const normalizeNativeAssetId = (assetId: string): AssetId => {
  const { chain, asset } = Parser.parseAssetId(assetId);
  const nativePlaceholder =
    chain.namespace === "eip155" &&
    asset.namespace === "erc20" &&
    asset.reference.toLowerCase() === EVM_NATIVE_TOKEN_PLACEHOLDER_ADDRESS;
  return Builder.buildAssetId(
    chain,
    nativePlaceholder
      ? { namespace: "slip44", reference: EVM_NATIVE_SLIP44_COIN_TYPE }
      : asset,
  );
};

/**
 * Parse legacy AssetIdFormat ("address:chainId") to AssetIdentifier
 * Auto-detects Solana vs EVM based on chainId.
 *
 * @example
 * ```ts
 * // EVM (auto-detected)
 * const evmIdentifier = assetIdToIdentifier("0x833589fcd6edb6e08f4c7c32d4f71b54bda02913:8453");
 * // Returns:
 * // {
 * //   chainNamespace: "eip155",
 * //   chainReference: "8453",
 * //   assetNamespace: "erc20",
 * //   assetReference: "0x833589fcd6edb6e08f4c7c32d4f71b54bda02913"
 * // }
 *
 * // Solana (auto-detected from chainId 1399811149)
 * const solanaIdentifier = assetIdToIdentifier("EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v:1399811149");
 * // Returns:
 * // {
 * //   chainNamespace: "solana",
 * //   chainReference: "mainnet",
 * //   assetNamespace: "spl",
 * //   assetReference: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v"
 * // }
 * ```
 *
 * @param assetId - Legacy format "address:chainId"
 * @returns AssetIdentifier
 * @throws Error if assetId format is invalid
 */
export const assetIdToIdentifier = (assetId: string): AssetIdentifier => {
  const parts = assetId.split(":");
  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    throw new Error(
      `Invalid assetId format: ${assetId}. Expected "address:chainId"`,
    );
  }

  const [address, chainId] = parts;

  // Auto-detect chain namespace from chainId
  const chainNamespace = detectChainNamespace(chainId);
  const namespace = NAMESPACES(chainNamespace);

  // For Solana, use the proper CAIP chain reference
  // For EVM, use the numeric chainId as-is
  const chainReference =
    chainNamespace === "solana" ? SOLANA_CHAIN_REFERENCE : chainId;

  return {
    chainNamespace: namespace.chainNamespace,
    chainReference: chainReference as ChainReference,
    assetNamespace: namespace.assetNamespace,
    assetReference: normalizeAssetReference(
      chainNamespace,
      address,
    ) as AssetReference,
  };
};

/**
 * The chain-native asset id (`eip155:<chain>/slip44:60`) for an EVM chain
 * ref. This is the canonical identity for native value everywhere an asset id
 * is expected — holds, accounting, and routing all key native by this form,
 * never by the internal placeholder address.
 */
export const evmNativeAssetIdForChainRef = (chainRef: string): string => {
  const parsed = Parser.parseChainId(chainRef).chain;
  return Builder.buildAssetId(parsed, {
    namespace: "slip44",
    reference: EVM_NATIVE_SLIP44_COIN_TYPE,
  });
};

/**
 * Convert AssetIdentifier back to legacy AssetIdFormat ("address:chainId")
 * Handles Solana chain reference conversion back to numeric chainId.
 */
export const identifierToAssetId = (identifier: AssetIdentifier): string => {
  if (identifier.chainNamespace === "sui") {
    throw new CAIPValidationError(
      "Sui coin identifiers cannot be represented as legacy address:chainId asset IDs.",
      `${identifier.chainNamespace}:${identifier.chainReference}/${identifier.assetNamespace}:${identifier.assetReference}`,
    );
  }

  // For Solana, convert the CAIP chain reference back to numeric chainId
  const chainId =
    identifier.chainNamespace === "solana"
      ? SOLANA_CHAIN_ID.toString()
      : identifier.chainReference;

  if (
    identifier.chainNamespace === "eip155" &&
    identifier.assetNamespace === "slip44" &&
    identifier.assetReference === EVM_NATIVE_SLIP44_COIN_TYPE
  ) {
    return `${EVM_NATIVE_TOKEN_PLACEHOLDER_ADDRESS}:${chainId}`;
  }

  if (
    identifier.chainNamespace === "solana" &&
    identifier.assetNamespace === "slip44" &&
    identifier.assetReference === SOLANA_NATIVE_SLIP44_COIN_TYPE
  ) {
    return `${SOLANA_NATIVE_TOKEN_ADDRESS}:${chainId}`;
  }

  return `${identifier.assetReference}:${chainId}`;
};

const parsedCaipAssetIdToIdentifier = (assetId: string): AssetIdentifier => {
  const parsed = Parser.parseAssetId(assetId);

  return {
    chainNamespace: parsed.chain.namespace,
    chainReference: parsed.chain.reference,
    assetNamespace: parsed.asset.namespace,
    assetReference: normalizeAssetReference(
      parsed.chain.namespace,
      parsed.asset.reference,
    ) as AssetReference,
  };
};

/**
 * Parse either a legacy AssetIdFormat ("address:chainId") or a CAIP-19 asset id
 * into a normalized AssetIdentifier.
 */
export const assetIdLikeToIdentifier = (assetId: string): AssetIdentifier => {
  try {
    return assetIdToIdentifier(assetId);
  } catch {
    return parsedCaipAssetIdToIdentifier(assetId);
  }
};

/**
 * Convert either a legacy AssetIdFormat or a CAIP-19 asset id into normalized
 * legacy AssetIdFormat for equality checks and lookups.
 */
export const assetIdLikeToLegacyAssetId = (assetId: string): string =>
  identifierToAssetId(assetIdLikeToIdentifier(assetId));

export const tryAssetIdLikeToLegacyAssetId = (
  assetId: string,
): string | null => {
  try {
    return assetIdLikeToLegacyAssetId(assetId);
  } catch {
    return null;
  }
};

/**
 * Compare two asset ids that may be in legacy or CAIP-19 format.
 */
export const assetIdLikeEquals = (left: string, right: string): boolean => {
  const normalizedLeft = tryAssetIdLikeToLegacyAssetId(left);
  const normalizedRight = tryAssetIdLikeToLegacyAssetId(right);

  if (normalizedLeft && normalizedRight) {
    return normalizedLeft === normalizedRight;
  }

  return left === right;
};

/**
 * Extract the numeric legacy chain id from either a legacy AssetIdFormat or a
 * CAIP-19 asset id.
 */
export const getChainIdFromAssetIdLike = (assetId: string): string | null => {
  const legacyAssetId = tryAssetIdLikeToLegacyAssetId(assetId);
  if (!legacyAssetId) return null;

  const separatorIndex = legacyAssetId.lastIndexOf(":");
  if (separatorIndex === -1) return null;

  return legacyAssetId.slice(separatorIndex + 1);
};
